// redux/selectors/ugc/momentSelectors.ts

import { createSelector } from "@reduxjs/toolkit";
import type { RootState } from "../../storeTypes";
import type { MomentDTO } from "../../../types/ugc/momentTypes";
import {
  getMomentCoverFieldName,
  getMomentImageFieldNames,
  getMomentImageItems,
  getMomentMediaType,
  isMultiPhotoMoment,
  isPhotoMoment,
  isVideoMoment,
} from "../../../types/ugc/momentTypes";

// ------------------------------------------------------------
// Base selectors
// ------------------------------------------------------------
const selectMomentState = (state: RootState) => state.moments;

const selectBySlugMap = (state: RootState) => state.moments.bySlug;


// ------------------------------------------------------------
// Single moment selectors
// ------------------------------------------------------------
export const selectMomentBySlug = (slug?: string) =>
  createSelector(
    [selectBySlugMap],
    (bySlug): MomentDTO | undefined =>
      slug ? bySlug[slug] : undefined
  );


// ------------------------------------------------------------
// Feed selectors (cursor-based)
// ------------------------------------------------------------
export const selectMomentFeedIds = createSelector(
  [selectMomentState],
  (moments) => moments.feed.ids
);

export const selectMomentFeed = createSelector(
  [selectMomentFeedIds, selectBySlugMap],
  (ids, bySlug): MomentDTO[] =>
    ids.map((slug) => bySlug[slug]).filter(Boolean)
);

export const selectMomentFeedLoading = createSelector(
  [selectMomentState],
  (moments) => moments.feed.loading
);

export const selectMomentFeedNextCursor = createSelector(
  [selectMomentState],
  (moments) => moments.feed.next
);


// ------------------------------------------------------------
// Explore selectors (page-based)
// ------------------------------------------------------------
export const selectMomentExploreIds = createSelector(
  [selectMomentState],
  (moments) => moments.explore.ids
);

export const selectMomentExplore = createSelector(
  [selectMomentExploreIds, selectBySlugMap],
  (ids, bySlug): MomentDTO[] =>
    ids.map((slug) => bySlug[slug]).filter(Boolean)
);

export const selectMomentExploreLoading = createSelector(
  [selectMomentState],
  (moments) => moments.explore.loading
);

export const selectMomentExploreHasMore = createSelector(
  [selectMomentState],
  (moments) => moments.explore.hasMore
);


// ------------------------------------------------------------
// My moments selectors
// ------------------------------------------------------------
export const selectMyMomentIds = createSelector(
  [selectMomentState],
  (moments) => moments.me.ids
);

export const selectMyMoments = createSelector(
  [selectMyMomentIds, selectBySlugMap],
  (ids, bySlug): MomentDTO[] =>
    ids.map((slug) => bySlug[slug]).filter(Boolean)
);

export const selectMyMomentsLoading = createSelector(
  [selectMomentState],
  (moments) => moments.me.loading
);

export const selectMyMomentsHasMore = createSelector(
  [selectMomentState],
  (moments) => moments.me.hasMore
);


// ------------------------------------------------------------
// Status selectors
// ------------------------------------------------------------
export const selectMomentCreateStatus = createSelector(
  [selectMomentState],
  (moments) => moments.createStatus
);

export const selectMomentDeleteStatus = createSelector(
  [selectMomentState],
  (moments) => moments.deleteStatus
);

export const selectMomentUpdateStatus = createSelector(
  [selectMomentState],
  (moments) => moments.updateStatus
);

export const selectMomentOpError = createSelector(
  [selectMomentState],
  (moments) => moments.opError
);


// ------------------------------------------------------------
// Media helper selectors
// ------------------------------------------------------------
export const selectMomentMediaInfo = (slug?: string) =>
  createSelector(
    [selectBySlugMap],
    (bySlug) => {
      const moment = slug ? bySlug[slug] : undefined;
      if (!moment) return undefined;

      return {
        mediaType: getMomentMediaType(moment),
        isPhoto: isPhotoMoment(moment),
        isVideo: isVideoMoment(moment),
        isMultiPhoto: isMultiPhotoMoment(moment),
        imageItems: getMomentImageItems(moment),
        coverFieldName: getMomentCoverFieldName(moment),
        imageFieldNames: getMomentImageFieldNames(moment),
      };
    }
  );